import { useId, useState } from 'react';
import { cssVar } from '@going-once/tokens';
import { focusRing, Stack, Text } from './primitives';

export interface ComboOption {
  id: string;
  label: string;
  /** A second line: who they are, so two of the same name can be told apart. */
  detail?: string | undefined;
}

export type ComboKey = 'ArrowDown' | 'ArrowUp' | 'Home' | 'End';

/**
 * Where the highlight goes on a key. **No option highlighted is `-1`**, so the first ArrowDown lands on the
 * first option and the first ArrowUp on the last, and Enter with nothing highlighted picks nothing.
 */
export function comboStep(count: number, active: number, key: ComboKey): number {
  if (count === 0) return -1;
  switch (key) {
    case 'ArrowDown':
      return active >= count - 1 ? 0 : active + 1;
    case 'ArrowUp':
      return active <= 0 ? count - 1 : active - 1;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
  }
}

/**
 * **A text box that suggests** (the design system's `Combobox`, list presentation): the caller owns the
 * text and the results (a search it runs as the text changes), and this owns the keyboard and the
 * listbox. Arrow keys move through the results, Enter picks the highlighted one, Escape closes. A pick
 * calls `onPick`; typing again after a pick is a new search.
 *
 * The ARIA combobox pattern: the input keeps focus and names the highlighted option with
 * `aria-activedescendant`, so a screen reader reads each result as the arrows pass it.
 */
export function Combobox({
  id,
  label,
  noun,
  value,
  onChange,
  options,
  onPick,
  loading,
  emptyText,
  placeholder,
  disabled,
  note,
}: {
  id: string;
  label: string;
  /** What the options are, for the empty message: "No person matches". */
  noun: string;
  /** Says why nothing matched, when the caller knows (an email must be whole). Replaces "No {noun} matches." */
  emptyText?: string | undefined;
  value: string;
  onChange: (text: string) => void;
  options: readonly ComboOption[];
  onPick: (option: ComboOption) => void;
  loading?: boolean;
  placeholder?: string;
  disabled?: boolean;
  note?: string;
}) {
  const listId = `${useId()}-list`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [focused, setFocused] = useState(false);
  const showing = open && !disabled && (loading === true || options.length > 0 || value.trim().length > 0);

  const pick = (option: ComboOption) => {
    onPick(option);
    setOpen(false);
    setActive(-1);
  };

  return (
    <Stack gap="xs">
      <Text as="label" step="label" tone="fg.muted" htmlFor={id}>
        {label}
      </Text>
      <div style={{ position: 'relative' }}>
        <input
          id={id}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showing}
          aria-controls={listId}
          aria-activedescendant={showing && active >= 0 ? `${listId}-${active}` : undefined}
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={(event) => {
            setFocused(true);
            setOpen(true);
            // A value already in the box ("Me") is replaced by typing, not typed after.
            event.currentTarget.select();
          }}
          onBlur={() => {
            setFocused(false);
            setOpen(false);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setOpen(false);
              return;
            }
            if (event.key === 'Enter') {
              const chosen = active >= 0 ? options[active] : undefined;
              if (showing && chosen) {
                event.preventDefault();
                pick(chosen);
              }
              return;
            }
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Home' || event.key === 'End') {
              if (event.key === 'Home' || event.key === 'End') return;
              event.preventDefault();
              setOpen(true);
              setActive((current) => comboStep(options.length, current, event.key as ComboKey));
            }
          }}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            background: cssVar('color.bg.base'),
            color: cssVar('color.fg.default'),
            borderRadius: cssVar('radius.md'),
            padding: cssVar('space.sm'),
            minHeight: cssVar('size.target.min'),
            fontFamily: cssVar('family.base'),
            fontSize: cssVar('type.body.size'),
            border: `1px solid ${cssVar('color.border.default')}`,
            ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
            ...(focused ? focusRing : {}),
          }}
        />
        {showing ? (
          <div
            style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: cssVar('space.xs') }}
          >
            <div
              style={{
                background: cssVar('color.bg.sheet'),
                border: `1px solid ${cssVar('color.border.strong')}`,
                borderRadius: cssVar('radius.md'),
                boxShadow: cssVar('elevation.overlay'),
              }}
            >
              <div
                id={listId}
                role="listbox"
                aria-label={label}
                tabIndex={-1}
                style={{ padding: cssVar('space.xs'), maxHeight: cssVar('size.menu.max'), overflowY: 'auto' }}
              >
                {loading ? (
                  <div style={{ padding: cssVar('space.sm') }}>
                    <Text tone="fg.muted">Searching…</Text>
                  </div>
                ) : options.length === 0 ? (
                  <div style={{ padding: cssVar('space.sm') }}>
                    <Text tone="fg.muted">{emptyText ?? `No ${noun} matches.`}</Text>
                  </div>
                ) : (
                  options.map((option, index) => (
                    <div
                      key={option.id}
                      id={`${listId}-${index}`}
                      role="option"
                      tabIndex={-1}
                      aria-selected={index === active}
                      // Before the input's blur, which would close the list and swallow the click.
                      onMouseDown={(event) => {
                        event.preventDefault();
                        pick(option);
                      }}
                      onMouseEnter={() => setActive(index)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: cssVar('space.xs'),
                        padding: cssVar('space.sm'),
                        borderRadius: cssVar('radius.sm'),
                        cursor: 'pointer',
                        background: index === active ? cssVar('color.neutral.tint') : 'transparent',
                      }}
                    >
                      <Text>{option.label}</Text>
                      {option.detail ? (
                        <Text step="label" tone="fg.muted">
                          {option.detail}
                        </Text>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {note ? (
        <Text step="label" tone="fg.muted">
          {note}
        </Text>
      ) : null}
    </Stack>
  );
}
